import { useCallback, useEffect, useState } from "react";
import type React from "react";
import { api } from "../../api/client";
import { theme, card, emptyFrame, btnPrimary, btnSecondary } from "../../theme";
import { useConsultantShell } from "./ConsultantShellContext";
import { errText, NOTE_KIND_LABELS, todayKey, type EngagementNote } from "./workspace";
import { fmtDateOnly } from "../../lib/dateOnly";

// ── Engagement notes ──────────────────────────────────────────────────────
// The consultancy's own timeline for a client: calls, meetings, decisions and
// follow-ups. Private to the firm — never shown to the client, never sent to the
// AI. A follow-up date puts the note on Today until someone ticks it off.

export function EngagementNotesTab({ orgId }: { orgId: string }) {
  const { user, isFirmAdmin } = useConsultantShell();
  const [notes, setNotes] = useState<EngagementNote[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [kind, setKind] = useState<EngagementNote["kind"]>("call");
  const [body, setBody] = useState("");
  const [occurredAt, setOccurredAt] = useState(todayKey());
  const [followUpAt, setFollowUpAt] = useState("");
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState<"" | "open">("");
  // The note being corrected in place: its words, date and follow-up date (author or firm admin).
  const [editing, setEditing] = useState<{ id: string; body: string; occurredAt: string; followUpAt: string } | null>(null);

  const load = useCallback(() => {
    api.get<{ notes: EngagementNote[] }>(`/consultant/workspace/clients/${orgId}/notes`)
      .then((d) => { setNotes(d.notes); setError(null); })
      .catch((e) => { setError(errText(e, "Could not load notes")); setNotes([]); });
  }, [orgId]);
  useEffect(load, [load]);

  /** The same rules the server applies (lib/workRecordDates.ts), said before anything is sent. */
  const datesError = (on: string, followUp: string, followUpWas?: string): string | null => {
    const today = todayKey();
    if (!on) return "Choose the date it happened.";
    if (on > today) return "The date it happened cannot be in the future.";
    if (followUp && followUp !== followUpWas && followUp < today) return "The follow-up date cannot be in the past.";
    return null;
  };

  const add = () => {
    if (!body.trim()) return;
    const problem = datesError(occurredAt, followUpAt);
    if (problem) { setError(problem); return; }
    setError(null);
    setSaving(true);
    api.post(`/consultant/workspace/clients/${orgId}/notes`, { kind, body: body.trim(), occurredAt, followUpAt: followUpAt || null })
      .then(() => { setBody(""); setFollowUpAt(""); load(); })
      .catch((e) => setError(errText(e, "Could not save the note")))
      .finally(() => setSaving(false));
  };
  const patch = (id: string, data: Record<string, unknown>) =>
    api.patch(`/consultant/workspace/notes/${id}`, data).then(load).catch((e) => setError(errText(e, "Could not update the note")));
  const saveEdit = () => {
    if (!editing || !editing.body.trim()) return;
    const was = notes?.find((n) => n.id === editing.id)?.followUpAt?.slice(0, 10) ?? "";
    const problem = datesError(editing.occurredAt, editing.followUpAt, was);
    if (problem) { setError(problem); return; }
    setError(null);
    setSaving(true);
    api.patch(`/consultant/workspace/notes/${editing.id}`, {
      body: editing.body.trim(), occurredAt: editing.occurredAt, followUpAt: editing.followUpAt || null,
    })
      .then(() => { setEditing(null); load(); })
      .catch((e) => setError(errText(e, "Could not update the note")))
      .finally(() => setSaving(false));
  };
  const remove = (id: string) => {
    if (!window.confirm("Delete this note? This cannot be undone.")) return;
    api.del(`/consultant/workspace/notes/${id}`).then(load).catch((e) => setError(errText(e, "Could not delete the note")));
  };

  const input: React.CSSProperties = { padding: "7px 9px", borderRadius: 3, border: `1px solid ${theme.color.borderStrong}`, background: theme.color.panel, color: theme.color.text, fontSize: 13, fontFamily: theme.font };
  const shown = (notes ?? []).filter((n) => filter !== "open" || (n.followUpAt && !n.followUpDoneAt));
  const today = todayKey();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ ...card, display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: theme.color.heading }}>Add to the engagement record</div>
        <div style={{ fontSize: 12, color: theme.color.subtle }}>Only your firm sees these notes. The client never does, and they are never sent to the AI.</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <select value={kind} onChange={(e) => setKind(e.target.value as EngagementNote["kind"])} aria-label="Kind of note" style={input}>
            {(Object.keys(NOTE_KIND_LABELS) as EngagementNote["kind"][]).map((k) => <option key={k} value={k}>{NOTE_KIND_LABELS[k]}</option>)}
          </select>
          <label style={{ fontSize: 12, color: theme.color.subtle, display: "flex", gap: 6, alignItems: "center" }}>
            On <input type="date" value={occurredAt} max={today} onChange={(e) => setOccurredAt(e.target.value)} style={input} />
          </label>
          <label style={{ fontSize: 12, color: theme.color.subtle, display: "flex", gap: 6, alignItems: "center" }}>
            Follow up by <input type="date" value={followUpAt} min={today} onChange={(e) => setFollowUpAt(e.target.value)} style={input} />
          </label>
        </div>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} maxLength={4000} placeholder="What was discussed or decided, and what happens next"
          aria-label="Note" style={{ ...input, resize: "vertical" }} />
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button onClick={add} disabled={saving || !body.trim()} style={{ ...btnPrimary, padding: "7px 16px", fontSize: 12.5, opacity: saving || !body.trim() ? 0.6 : 1 }}>{saving ? "Saving…" : "Add note"}</button>
        </div>
        {error && <div role="alert" style={{ fontSize: 12.5, color: theme.color.dangerText }}>{error}</div>}
      </div>

      <div style={{ display: "flex", gap: 6 }}>
        {([["", "Everything"], ["open", "Open follow-ups"]] as const).map(([k, label]) => (
          <button key={k || "all"} onClick={() => setFilter(k)} aria-pressed={filter === k}
            style={{ padding: "5px 12px", borderRadius: 3, fontSize: 12, fontWeight: 600, cursor: "pointer", border: `1px solid ${filter === k ? theme.color.accent : theme.color.border}`, background: filter === k ? theme.color.accentBg : "transparent", color: filter === k ? theme.color.accentText : theme.color.subtle }}>
            {label}
          </button>
        ))}
      </div>

      {notes === null && <div style={{ fontSize: 13, color: theme.color.subtle }}>Loading…</div>}
      {notes && shown.length === 0 && (
        <div style={{ ...emptyFrame, color: theme.color.subtle, fontSize: 13, padding: 28 }}>
          {filter === "open" ? "No follow-ups outstanding." : "Nothing recorded yet. Log calls, meetings and decisions here so the whole firm has the history."}
        </div>
      )}

      {shown.map((n) => {
        const mine = n.authorUserId === user.id;
        const openFollowUp = n.followUpAt && !n.followUpDoneAt;
        const overdue = openFollowUp && n.followUpAt!.slice(0, 10) < today;
        return (
          <div key={n.id} style={{ ...card, padding: "12px 16px", borderLeft: `3px solid ${overdue ? theme.color.danger : openFollowUp ? theme.color.warning : theme.color.border}` }}>
            <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
              <span style={{ fontSize: 10.5, fontWeight: 700, padding: "1px 8px", borderRadius: 3, background: theme.color.consultantBg, color: theme.color.consultant }}>{NOTE_KIND_LABELS[n.kind]}</span>
              <span style={{ fontSize: 12, color: theme.color.subtle }}>{fmtDateOnly(n.occurredAt)}</span>
              {n.authorName && <span style={{ fontSize: 12, color: theme.color.faint }}>by {n.authorName}</span>}
            </div>
            {editing?.id === n.id ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <label style={{ fontSize: 12, color: theme.color.subtle, display: "flex", gap: 6, alignItems: "center" }}>
                    On <input type="date" value={editing.occurredAt} max={today} onChange={(e) => setEditing({ ...editing, occurredAt: e.target.value })} style={input} />
                  </label>
                  <label style={{ fontSize: 12, color: theme.color.subtle, display: "flex", gap: 6, alignItems: "center" }}>
                    Follow up by <input type="date" value={editing.followUpAt} onChange={(e) => setEditing({ ...editing, followUpAt: e.target.value })} style={input} />
                  </label>
                </div>
                <textarea value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} rows={3} maxLength={4000}
                  aria-label="Note" style={{ ...input, resize: "vertical" }} />
                <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                  <button onClick={() => setEditing(null)} style={{ ...btnSecondary, padding: "5px 12px", fontSize: 12 }}>Cancel</button>
                  <button onClick={saveEdit} disabled={saving || !editing.body.trim() || !editing.occurredAt}
                    style={{ ...btnPrimary, padding: "5px 14px", fontSize: 12, opacity: saving || !editing.body.trim() ? 0.6 : 1 }}>{saving ? "Saving…" : "Save"}</button>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 13, color: theme.color.text, lineHeight: 1.55, whiteSpace: "pre-wrap", marginTop: 6 }}>{n.body}</div>
            )}
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
              {n.followUpAt && (
                <span style={{ fontSize: 12, fontWeight: 600, color: n.followUpDoneAt ? theme.color.successText : overdue ? theme.color.dangerText : theme.color.warningText }}>
                  {n.followUpDoneAt ? "Follow-up done" : `Follow up by ${fmtDateOnly(n.followUpAt)}${overdue ? " (overdue)" : ""}`}
                </span>
              )}
              <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                {n.followUpAt && (
                  <button onClick={() => patch(n.id, { followUpDone: !n.followUpDoneAt })} style={{ ...btnSecondary, padding: "3px 10px", fontSize: 11.5 }}>
                    {n.followUpDoneAt ? "Reopen follow-up" : "Mark done"}
                  </button>
                )}
                {(mine || isFirmAdmin) && editing?.id !== n.id && (
                  <button
                    onClick={() => setEditing({ id: n.id, body: n.body, occurredAt: n.occurredAt.slice(0, 10), followUpAt: n.followUpAt?.slice(0, 10) ?? "" })}
                    style={{ ...btnSecondary, padding: "3px 10px", fontSize: 11.5 }}
                  >
                    Edit
                  </button>
                )}
                {(mine || isFirmAdmin) && (
                  <button onClick={() => remove(n.id)} style={{ ...btnSecondary, padding: "3px 10px", fontSize: 11.5, color: theme.color.dangerText }}>Delete</button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
